import { Table, TableContainer, TableBody, TableHead, TableRow, TableCell } from "@mui/material";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TableVirtuoso, TableComponents } from "react-virtuoso";

import { RequestedVideo } from "@/api/requestedVideos";

import RequestedVideoRow from "../RequestedVideoRow/RequestedVideoRow";
import useStyles from "../RequestedVideosPage.styles";


export interface RequestedVideoTableProps {
    videos: RequestedVideo[];
    handleVideoRowClicked: (
        mouseEvent: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
        video: RequestedVideo,
    ) => void;
    liveUnitId: number;
}

interface TableContext {
    styles: ReturnType<typeof useStyles>;
    handleRowClicked: (mouseEvent: React.MouseEvent<HTMLTableRowElement, MouseEvent>, videoIdx: number) => void;
}

// arrow components complain about no display name.
/* eslint-disable prefer-arrow-callback */
const TC: TableComponents<TableContext> = {
    Table: ({ context, ...props }) => <Table {...props} className={context?.styles.table} />,
    TableHead: React.forwardRef(function TCTableHead({ ...props }, ref) {
        return <TableHead {...props} ref={ref} />;
    }),
    TableBody: React.forwardRef(function TCTableBody({ context, ...props }, ref) {
        return <TableBody {...props} ref={ref} className={context?.styles.body} />;
    }),
    TableRow: ({ context, ...props }) => (
        <TableRow
            {...props}
            id={`requested-videos_requested-video-row-${props["data-index"]}`}
            className={context?.styles.row}
            hover
            onClick={(e) => context?.handleRowClicked(e, props["data-index"])}
        />
    ),
    Scroller: React.forwardRef(function TCScroller({ context, ...props }, ref) {
        return <TableContainer component="div" ref={ref} {...props} className={context?.styles.container} />;
    }),
    EmptyPlaceholder: () => (
        <TableBody style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
            <TableRow data-testid="no-requested-videos-row">
                <TableCell>
                    <h1>No videos found</h1>
                </TableCell>
            </TableRow>
        </TableBody>
    ),
};
/* eslint-enable prefer-arrow-callback */

const RequestedVideosTable: React.FC<RequestedVideoTableProps> = ({
    videos,
    handleVideoRowClicked,
    liveUnitId,
}) => {
    const { t } = useTranslation("translation", { keyPrefix: "components.requestedVideos" });
    const styles = useStyles();

    const columns = [
        "", // thumbnail
        t("camera"),
        t("recorded"),
        t("requested"),
        t("requestedBy"),
        "", // share & download link
    ];

    /**
     * Head and Row components pulled out of render() to prevent unneccessary rerenders
     * https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/no-unstable-nested-components.md
     */
    const renderHead = () => {
        return (
            <TableRow className={styles.head}>
                {columns.map((column, i) => (
                    <TableCell key={i}>{column}</TableCell>
                ))}
            </TableRow>
        );
    };

    const renderRow = (index: number, row: RequestedVideo) => {
        return (
            <RequestedVideoRow
                liveUnitId={liveUnitId}
                row={row}
            />
        );
    };

    const handleRowClicked = useCallback(
        (e, idx) => handleVideoRowClicked(e, videos[idx]),
        [videos, handleVideoRowClicked],
    );

    const context: TableContext = {
        styles,
        handleRowClicked,
    };

    return (
        <TableVirtuoso
            data={videos}
            context={context}
            components={TC}
            fixedHeaderContent={renderHead}
            itemContent={renderRow}
        />
    );
};

export const MemoizedRequestedVideosTable = React.memo(RequestedVideosTable);
